import { AddToCartEventInterface, CheckoutInterface, GenerateLeadInterface, ItemListInterface, ItemsInterface, PaymentInfoInterface, PurchaseInterface, RemoveFromCartInterface, SelectItemInterface, ShippingInfoInterface, UserIdInterface, ViewItemInterface } from 'sih-gtm/build/TagManager'

import { CartItemFragment } from '@hooks/api'
import { convertEnumToNum, convertEnumToNumFRZN } from '@molecules/stores'
import { NumberOfPortionsEnum } from '@uctypes/api/globalTypes'

import type { CheckoutUiStatusGapContext } from './checkoutObservability'
import { AddressCheckoutStepInterface, CartSaveInterface, CheckoutCompletedInterface, CheckoutUiStatusGapInterface, ConfirmationInterface, CouponDetailsInterface, CreditCardCheckoutStepInterface, DeeplinkDetailsInterface, DeeplinkErrorInterface, GTMBase, LoginInfoInterface, PlanInterface, SearchItemInterface, SignupInfoInterface } from './GTMBase'
import { EventPlugin } from './UserEvents'

export interface ItemInterfaceNew extends ItemsInterface {
  item_id: string
  item_name: string
  item_image?: string
  item_brand?: string
  item_group_id?: string
  item_group_name?: string
  item_category?: string
  item_list_name?: string
  item_variant?: string
  item_stock_count?: number
  is_mealkit?: string
  item_chef?: string
  quantity?: number
  item_serving_size?: number
  price?: number
  index?: number
}

export interface ProductGroupInterface {
  prid: string
  image: string
  product_name: string
  product_url: string
}

export interface ProductGroupDataInterface {
  category_name: string
  category_id: string
  products: ProductGroupInterface[]
}
export class GTM extends GTMBase implements EventPlugin {

  private getCartProductQuantities = (cartItems: readonly CartItemFragment[]): number => {

    let productQuantities = 0
    for (const i in cartItems) {
      const cartItem = cartItems[i]
      productQuantities = productQuantities + cartItem?.quantity
    }
    return productQuantities
  }

  private createCartProducts = (cartItems: readonly CartItemFragment[]): ItemInterfaceNew[] => {
    const logProducts = [] as ItemInterfaceNew[]

    for (const i in cartItems) {
      const cartItem = cartItems[i]

      switch (cartItem.product.__typename) {

        case 'MealKit':
          const servingSizeMK = convertEnumToNum(cartItem?.product?.portionSize as unknown as NumberOfPortionsEnum)

          logProducts.push({
            item_name: cartItem?.product?.name,
            item_id: cartItem?.product?.id,
            item_group_id: cartItem?.product?.group?.id,
            price: cartItem?.price,
            item_brand: 'UCOOK',
            item_category: cartItem?.product?.mealKitCategories?.map((cat: any) => cat.id).join(', '),
            item_variant: cartItem?.product?.mealKitCategories?.map((cat: any) => cat.title).join(', '),
            item_list_name: 'Meal Kit',
            quantity: cartItem?.quantity,
            item_image: cartItem?.product?.coverImage?.location,
            item_stock_count: 1,
            item_chef: cartItem?.product?.chef?.name,
            item_serving_size: servingSizeMK,
            is_mealkit: 'yes',
          })
          break

        case 'FrozenMeal':
          const servingSizeFRZ = convertEnumToNumFRZN(cartItem?.product?.frozenPortionSize)

          logProducts.push({
            item_name: cartItem?.product?.name,
            item_id: cartItem?.product?.id,
            item_group_id: cartItem?.product?.group?.id,
            price: cartItem?.product?.price,
            item_brand: 'UCOOK',
            item_category: cartItem?.product?.frozenCategories?.map((cat: any) => cat.id).join(', '),
            item_variant: cartItem?.product?.frozenCategories?.map((cat: any) => cat.title)?.join(', '),
            item_list_name: 'Craft Meals',
            quantity: cartItem?.quantity,
            item_image: cartItem?.product?.coverImage?.location,
            item_stock_count: 1,
            item_chef: 'UCOOK',
            item_serving_size: servingSizeFRZ,
            is_mealkit: 'no',
          })
          break

        case 'Wine':
          logProducts.push({
            item_name: cartItem?.product?.name,
            item_id: cartItem?.product?.id,
            item_group_id: cartItem?.product?.id,
            price: cartItem?.product?.price,
            item_brand: cartItem?.product?.vineyard?.name,
            item_category: cartItem?.product?.wineCategory?.id,
            item_variant: cartItem?.product?.wineCategory?.title,
            item_list_name: 'Wines',
            quantity: cartItem?.quantity,
            item_image: cartItem?.product?.coverImage?.location,
            item_stock_count: 1,
            item_chef: 'UCOOK',
            item_serving_size: 1,
            is_mealkit: 'no',
          })
          break
        case 'MarketProduct':
          logProducts.push({
            item_name: cartItem?.product?.name,
            item_id: cartItem?.product?.id,
            item_group_id: cartItem?.product?.id,
            price: cartItem?.product?.price,
            item_brand: cartItem?.product?.brand?.name,
            item_category: cartItem?.product?.marketProductCategories[0]?.id,
            item_variant: cartItem?.product?.marketProductCategories[0]?.title,
            item_list_name: 'MarketProduct',
            quantity: cartItem?.quantity,
            item_image: cartItem?.product?.coverImage?.location,
            item_stock_count: 1,
            item_chef: 'UCOOK',
            item_serving_size: 1,
            is_mealkit: 'no',
          })
          break
      }
    }

    return logProducts
  }

  hasAddedPaymentMethod(step: string | number, option: string, products: readonly CartItemFragment[], value: number, discountTitles: string, discountValue: number, userId: string): void {

    const gaProducts = this.createCartProducts(products)
    try {
      const eventData: PaymentInfoInterface = {
        event: 'add_payment_info',
        eventParams: {
          currency: 'ZAR',
          value,
          coupon: discountTitles,
          // shipping_tier: 'next day, air,ground'
          items: gaProducts,
        },
        customEventParams: {
          totalDiscountAmount: discountValue,
          user_id: userId,
        },
      }
      this.addPaymentInfo(eventData)
    } catch (e) {
      console.error(e)
    }
  }

  hasAddedCreditCardCheckoutStep(productGroupData: ProductGroupDataInterface, userId: string, userEmail: string, checkoutFlowType?: string): void {
    try {
      const eventData: CreditCardCheckoutStepInterface = {
        event: 'add_credit_card',
        eventParams: {
        },
        customEventParams: {
          user_id: userId,
          user_email: userEmail,
          checkoutDropoffStep: 'add_credit_card',
          lastCheckoutStepDate: new Date().toISOString(),
          productGroupInfo: productGroupData,
          checkoutFlowType: checkoutFlowType || 'manual',
        },
      }
      this.addCreditCardCheckoutStep(eventData)
    } catch (e) {
      console.error(e)
    }
  }

  hasAddedShippingMethod(step: string | number, option: string, products: readonly CartItemFragment[], value: number, discountTitles: string, discountValue: number, userId: string): void {

    const gaProducts = this.createCartProducts(products)

    try {
      const eventData: ShippingInfoInterface = {
        event: 'add_shipping_info',
        eventParams: {
          currency: 'ZAR',
          value,
          coupon: discountTitles,
          // shipping_tier: 'next day, air,ground'
          items: gaProducts,
        },
        customEventParams: {
          totalDiscountAmount: discountValue,
          user_id: userId,
        },
      }
      this.addShippingInfo(eventData)

    } catch (e) {
      console.error(e)
    }

  }

  hasAddedAddressCheckoutStep(productGroupData: ProductGroupDataInterface, userId: string, isAddressSupported: boolean, userEmail: string, checkoutFlowType?: string): void {
    try {
      const eventData: AddressCheckoutStepInterface = {
        event: 'add_address',
        eventParams: {
        },
        customEventParams: {
          user_id: userId,
          user_email: userEmail,
          checkoutDropoffStep: 'address_added',
          lastCheckoutStepDate: new Date().toISOString(),
          isAddressSupported,
          productGroupInfo: productGroupData,
          checkoutFlowType: checkoutFlowType || 'manual',
        },
      }
      this.addAddressCheckoutStep(eventData)
    } catch (e) {
      console.error(e)
    }
  }

  hasLoggedUserId(userId: string): void {
    const eventData: UserIdInterface = {
      event: 'user_id_insert',
      eventParams: {
        user_id: userId,
      },
      customEventParams: {
        user_id: userId,
      },
    }

    try {
      this.addUserInfo(eventData)
    } catch (e) {
      console.error(e)
    }

  }

  hasAddedProductToCart(products: ItemInterfaceNew, userId?: string): void {
    try {
      const eventData: AddToCartEventInterface = {
        event: 'add_to_cart',
        eventParams: {
          currency: 'ZAR',
          value: products.price,
          items: [products],
        },
        customEventParams: {
          user_id: userId,
        },
      }
      this.addToCart(eventData)
    } catch (e) {
      console.error(e)
    }

  }

  hasRemovedProductFromCart(products: ItemInterfaceNew, userId?: string): void {
    try {
      const eventData: RemoveFromCartInterface = {
        event: 'remove_from_cart',
        eventParams: {
          currency: 'ZAR',
          value: products.price,
          items: [products],
        },
        customEventParams: {
          user_id: userId,
        },
      }
      this.removeFromCart(eventData)
    } catch (e) {
      console.error(e)
    }

  }

  hasViewedMultipleProducts(list: string, products: ItemInterfaceNew[], userId: string): void {
    const topThree = []
    const topThreeIds: string[] = []
    for (let i = 0; i < 3; i++) {
      if (products[i] !== undefined) {
        topThree.push(products[i])
        topThreeIds.push(products[i].item_id)
      }
    }

    try {
      const eventData: ViewItemInterface = {
        event: 'view_item',
        eventParams: {
          currency: 'ZAR',
          value: products[0].price,
          items: topThree,
        },
        customEventParams: {
          currentProductIds: topThreeIds,
          user_id: userId,
        },
      }

      this.viewItem(eventData)
    } catch (e) {
      console.error(e)
    }

  }

  hasBegunCheckoutStep(step: string | number, option: string, products: readonly CartItemFragment[], totalValue: number, discountTitles: string, discountValue: number, userId: string): void {
    const totalQuantity = this.getCartProductQuantities(products)
    const value = totalValue-discountValue
    const gaProducts = this.createCartProducts(products)
    const eventData: CheckoutInterface = {
      event: 'begin_checkout',
      eventParams: {
        currency: 'ZAR',
        value,
        coupon: discountTitles,
        items: gaProducts,
      },
      customEventParams: {
        option,
        totalDiscountAmount: discountValue,
        user_id: userId,
        undiscountedPrice: totalValue,
        totalProductQuantity: totalQuantity,
        source: 'UCOOK FRONT END',
        method: 'Card',
      },
    }
    try {
      this.beginCheckout(eventData)
    } catch (error) {
      console.error(error)
    }

  }

  hasConfirmedSubscriptionDetails(productGroupData: ProductGroupDataInterface, userId: string, userEmail: string, checkoutFlowType?: string): void {
    const eventData: ConfirmationInterface = {
      event: 'subscription_details_confirmation',
      eventParams: {
        currency: 'ZAR',
      },
      customEventParams: {
        productGroupInfo: productGroupData,
        user_id: userId,
        user_email: userEmail,
        checkoutDropoffStep: 'subscription_details_confirmation',
        lastCheckoutStepDate: new Date().toISOString(),
        checkoutFlowType: checkoutFlowType || 'manual',
      },
    }
    try {
      this.subscriptionDetailsConfirmation(eventData)
    } catch (error) {
      console.error(error)
    }
  }

  hasSelectedPlan(productGroupData: ProductGroupDataInterface, userId: string, userEmail: string, checkoutFlowType?: string): void {

    const eventData: PlanInterface = {
      event: 'plan_selected',
      eventParams: {
        plan: productGroupData.category_name,
      },
      customEventParams: {
        user_id: userId,
        user_email: userEmail,
        checkoutDropoffStep: 'plan_selected',
        lastCheckoutStepDate: new Date().toISOString(),
        productGroupInfo: productGroupData,
        checkoutFlowType: checkoutFlowType || 'manual',
      },
    }
    try {
      this.planSelected(eventData)
    } catch (error) {
      console.error(error)
    }
  }

  hasCompletedCheckoutFlow(productGroupData: ProductGroupDataInterface, userId: string, userEmail: string, checkoutFlowType?: string): void {

    const eventData: CheckoutCompletedInterface = {
      event: 'checkout_completed',
      eventParams: {
        currency: 'ZAR',
      },
      customEventParams: {
        user_id: userId,
        user_email: userEmail,
        checkoutDropoffStep: 'checkout_completed',
        lastCheckoutStepDate: new Date().toISOString(),
        productGroupInfo: productGroupData,
        checkoutFlowType: checkoutFlowType || 'manual',
      },
    }
    try {
      this.checkoutCompleted(eventData)
    } catch (error) {
      console.error(error)
    }
  }

  hasSavedCart(products: readonly CartItemFragment[], totalValue: number, discountTitles: string, discountValue: number, userId: string): void {
    const totalQuantity = this.getCartProductQuantities(products)
    const value = totalValue
    const gaProducts = this.createCartProducts(products)

    const eventData: CartSaveInterface = {
      event: 'cart_save',
      eventParams: {
        currency: 'ZAR',
        value,
        coupon: discountTitles,
        items: gaProducts,
      },
      customEventParams: {
        totalDiscountAmount: discountValue,
        user_id: userId,
        undiscountedPrice: totalValue + discountValue,
        totalProductQuantity: totalQuantity,
      },
    }
    try {
      this.saveCart(eventData)
    } catch (error) {
      console.error(error)
    }

  }

  hasAddedUserId(userId: string): void {

    try {
      const eventData: UserIdInterface = {
        event: 'user_id_insert',
        eventParams: {
          user_id: userId,
        },
      }

      this.addUserInfo(eventData)
    } catch (e) {
      console.error(e)
    }
  }

  hasMadePurchase(step: string | number, option: string, products: readonly CartItemFragment[], id: string, revenue: number, discountTitles: string, discountValue: number, userId: string): void {

    const gaProducts = this.createCartProducts(products)
    const totalQuantity = this.getCartProductQuantities(products)
    try {
      const eventData: PurchaseInterface = {
        event: 'purchase',
        eventParams: {
          currency: 'ZAR',
          transaction_id: id,
          value: revenue,
          coupon: discountTitles,
          items: gaProducts,
        },
        customEventParams: {
          option,
          totalDiscountValue: discountValue,
          user_id: userId,
          totalProductQuantity: totalQuantity,
          source: 'UCOOK FRONT END',
          method: 'Card',
          retry: false,
        },
      }
      this.purchase(eventData)
    } catch (e) {
      console.error(e)
    }
  }

  hasPerformedSearch(term: string): void {
    try {

      const eventData: SearchItemInterface = {
        event: 'search',
        eventParams: {
          term,
        },
      }

      this.search(eventData)
    } catch (e) {
      console.error(e)
    }
  }

  hasSelectedItem(list: string, data: ItemInterfaceNew, userId: string): void {

    const eventData: SelectItemInterface = {
      event: 'select_item',
      eventParams: {
        item_list_name: list,
        items: [data],
      },
      customEventParams: {
        currentProductId: data.item_id,
        user_id: userId,
      },
    }
    try {
      this.selectItem(eventData)
    } catch (e) {
      console.error(e)
    }
  }

  hasSelectedMultipleItems(list: string, data: ItemInterfaceNew[], userId: string): void {
    const topThree = []
    const topThreeIds: string[] = []
    for (let i = 0; i < 3; i++) {
      if (data[i] !== undefined) {
        topThree.push(data[i])
        topThreeIds.push(data[i].item_id)
      }
    }

    const eventData: SelectItemInterface = {
      event: 'select_item',
      eventParams: {
        item_list_name: list,
        items: topThree,
      },
      customEventParams: {
        currentProductIds: topThreeIds,
        user_id: userId,
      },

    }
    try {
      this.selectItem(eventData)
    } catch (e) {
      console.error(e)
    }
  }

  hasSignedUp(email: string, mobile: string, method: string, firstName: string, lastName: string, newsletter: string, source: string): void {
    try {

      if (!!mobile && mobile?.startsWith('+')) {
        mobile = mobile.substring(1)
      }

      const cleanedNumber = mobile?.replace(/\s+/g, '')

      const eventData: SignupInfoInterface = {
        event: 'sign_up',
        eventParams: {
          email,
          mobile: cleanedNumber,
          method,
          firstName,
          lastName,
          newsletter,
          source,
        },
        customEventParams: {
          checkoutDropoffStep: 'signed_up',
          lastCheckoutStepDate: new Date().toISOString(),
        },
      }

      this.signUp(eventData)
    } catch (e) {
      console.error(e)
    }
  }

  hasLoggedIn(userId: string, method: string, source: string): void {
    try {
      const eventData: LoginInfoInterface = {
        event: 'login',
        eventParams: {
          userId,
          method,
          source,
        },
      }

      this.login(eventData)
    } catch (e) {
      console.error(e)
    }
  }

  hasGeneratedLead(email: string, mobile: string, method: string): void {
    try {
      const eventData: GenerateLeadInterface = {
        event: 'generate_lead',
        eventParams: {
          currency: 'ZAR',
          value: 1,
        },
        customEventParams: {
          email,
          mobile,
          method,
        },
      }

      this.generateLead(eventData)
    } catch (e) {
      console.error(e)
    }
  }

  hasViewedProduct(list: string, products: ItemInterfaceNew, userId: string): void {

    try {
      const eventData: ViewItemInterface = {
        event: 'view_item',
        eventParams: {
          currency: 'ZAR',
          value: products.price,
          items: [products],
        },
        customEventParams: {
          currentProductId: products.item_id,
          user_id: userId,
        },
      }

      this.viewItem(eventData)
    } catch (e) {
      console.error(e)
    }
  }

  hasViewedCatalogue(products: ItemInterfaceNew[], userId: string): void {
    const productList: ItemInterfaceNew[] = []

    if (!products) {
      return
    }

    for (let i = 0; i < products.length; i++) {
      if (products[i] !== undefined) {
        productList.push({
          item_name: products[i].item_name,
          item_id: products[i].item_id,
          item_group_id: products[i].item_group_id,
          item_category: products[i].item_category,
          price: products[i].price,
          item_brand: products[i].item_brand,
          item_variant: products[i].item_variant,
          item_list_name: products[i].item_list_name,
          index: products[i].index,
          item_image: products[i]?.item_image,
        })
      }
    }

    try {
      const eventData: ItemListInterface = {
        event: 'view_item_list',
        eventParams: {
          item_list_name: products[0]?.item_list_name,
          items: productList,
        },
        customEventParams: {
          user_id: userId,
        },
      }
      this.viewItemList(eventData)
    } catch (e) {
      console.error(e)
    }

  }

  hasAddedToCart(products: ItemInterfaceNew, userId?: string): void {

    try {
      const eventData: AddToCartEventInterface = {
        event: 'add_to_cart',
        eventParams: {
          currency: 'ZAR',
          value: products.price,
          items: [products],
        },
        customEventParams: {
          user_id: userId,
        },
      }
      this.addToCart(eventData)
    } catch (e) {
      console.error(e)
    }

  }

  hasRemovedFromCart(products: ItemInterfaceNew, userId?: string): void {

    try {
      const eventData: RemoveFromCartInterface = {
        event: 'remove_from_cart',
        eventParams: {
          currency: 'ZAR',
          value: products.price,
          items: [products],
        },
        customEventParams: {
          user_id: userId,
        },
      }
      this.removeFromCart(eventData)
    } catch (e) {
      console.error(e)
    }

  }

  hasAddedCoupon(couponName: string, couponType: string): void {
    try {
      const eventData: CouponDetailsInterface = {
        event: 'coupon-insert',
        eventParams: {
          couponName,
          couponType,
        },
      }
      this.addCoupon(eventData)
    } catch (e) {
      console.error(e)
    }
  }

  hasOpenedAppWithDeeplink(userId: string, coldStart: boolean, deeplink: string): void {
    try {
      const eventData: DeeplinkDetailsInterface = {
        event: 'app-opened-with-deeplink',
        eventParams: {
          coldStart,
          deeplink,
        },
        customEventParams: {
          user_id: userId,
        },
      }
      this.openedAppWithDeeplink(eventData)
    } catch (e) {
      console.error(e)
    }
  }

  hasOpenedAppWithDeeplinkError(userId: string, coldStart: boolean, deeplink: string, error: Error): void {
    try {
      const eventData: DeeplinkErrorInterface = {
        event: 'app-opened-with-deeplink-error',
        eventParams: {
          coldStart,
          deeplink,
          error,
        },
        customEventParams: {
          user_id: userId,
        },
      }
      this.openedAppWithDeeplinkError(eventData)
    } catch (e) {
      console.error(e)
    }
  }

  hasCheckoutUiStatusGap(context: CheckoutUiStatusGapContext): void {
    try {
      const eventData: CheckoutUiStatusGapInterface = {
        event: 'checkout_ui_status_gap',
        eventParams: {
          surface: context.surface,
          reason: context.reason,
          pathname: typeof window !== 'undefined' ? window.location.pathname : '',
          sessionFlowType: context.sessionFlowType ?? null,
          stateFlowType: context.stateFlowType ?? null,
          hasCurrentUser: context.hasCurrentUser ?? false,
          hasCheckoutStatusId: context.hasCheckoutStatusId ?? false,
          isGuest: context.isGuest ?? false,
          isHydrated: context.isHydrated ?? false,
          isCheckoutStatusLoading: context.isCheckoutStatusLoading ?? false,
        },
      }
      this.checkoutUiStatusGap(eventData)
    } catch (e) {
      console.error(e)
    }
  }

}
